import React from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";

const Solutions = () => {
    return (
        <Layout>
            <section className="page-hero">
                <div className="container">
                    <div className="eyebrow"><span className="dot"></span> Solutions</div>
                    <h1>Solutions built for payment flows</h1>
                    <p>Four payment products. Three packaging options. One embedded operating stack.</p>
                    
                    <div className="page-subnav">
                        <a href="#fiat-to-crypto">Fiat-to-crypto</a>
                        <a href="#crypto-payments">Crypto payments</a>
                        <a href="#fiat">Fiat</a>
                        <a href="#stablecoins">Stablecoins</a>
                        <a href="#packages">Packages</a>
                    </div>
                </div>
            </section>

            <section className="section section-tight" id="fiat-to-crypto">
                <div className="container solution-block">
                    <div className="feature-card">
                        <span className="kicker">Solution 01</span>
                        <h2>Fiat-to-crypto</h2>
                        <p>Support acceptance of cards, APMs, and banking methods via partners for wallet funding and purchase flows.</p>
                        <ul className="check-list micro-list">
                            <li><strong>Features:</strong> multi-rail acceptance, onboarding, wallet delivery</li>
                            <li><strong>Benefits:</strong> broader acceptance, more funding choice, lower-cost routing options</li>
                            <li><strong>Processing types:</strong> cards, APMs, direct banking</li>
                        </ul>
                        <div className="packages-strip">
                            <span className="tag">API</span>
                            <span className="tag">Widget / iFrame</span>
                            <span className="tag">White-label EverWallet</span>
                        </div>
                        <div className="risk-card">
                            <strong>Risk disclosure</strong>
                            Availability depends on jurisdiction, onboarding, and Everest’s current service permissions. See{" "}
                            <Link to="/important-disclosures-risks">Important Disclosures &amp; Risks</Link>.
                        </div>
                    </div>
                    <div className="infra-band example-card">
                        <span className="kicker">Use case</span>
                        <h3>Universal funding into one wallet flow</h3>
                        <p>A platform adds Everest at checkout so customers can choose card, local payment methods, or bank transfer in one funding journey.</p>
                        <div className="case-metrics">
                            <span className="metric-pill">Broader acceptance coverage</span>
                            <span className="metric-pill">Potential for improved conversion, depending on implementation and market conditions</span>
                            <span className="metric-pill">Potential for optimised fee structures, depending on routing and usage</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="crypto-payments">
                <div className="container solution-block">
                    <div className="feature-card">
                        <span className="kicker">Solution 02</span>
                        <h2>Crypto payments</h2>
                        <p>Enable merchant crypto acceptance workflows, OTC support, and direct-to-consumer crypto payment flows.</p>
                        <ul className="check-list micro-list">
                            <li><strong>Features:</strong> payment acceptance, status tracking, settlement workflows</li>
                            <li><strong>Benefits:</strong> more payment choice, simpler operations, configurable settlement workflows</li>
                            <li><strong>Processing types:</strong> merchant processing, OTC, D2C</li>
                        </ul>
                        <div className="packages-strip">
                            <span className="tag">API</span>
                            <span className="tag">Widget / iFrame</span>
                            <span className="tag">White-label EverWallet</span>
                        </div>
                        <div className="risk-card">
                            <strong>Risk disclosure</strong>
                            Crypto-asset services involve significant risk, including market volatility, liquidity risk, and the potential loss of value.
                        </div>
                    </div>
                    <div className="infra-band example-card">
                        <span className="kicker">Use case</span>
                        <h3>Crypto acceptance with merchant control</h3>
                        <p>A merchant offers crypto at checkout, tracks payment status in real time, and manages settlement through Everest operations.</p>
                        <div className="case-metrics">
                            <span className="metric-pill">Additional payment method</span>
                            <span className="metric-pill">Less manual handling</span>
                            <span className="metric-pill">Controlled processing flow</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="fiat">
                <div className="container solution-block">
                    <div className="feature-card">
                        <span className="kicker">Solution 03</span>
                        <h2>Fiat</h2>
                        <p>vIBANs, e-wallets, and payment-account functionality, provided through licensed partners where applicable. Everest is not a bank and does not provide deposit-taking services.</p>
                        <ul className="check-list micro-list">
                            <li><strong>Features:</strong> named accounts, fiat balances, send / receive tools</li>
                            <li><strong>Benefits:</strong> higher retention, more daily utility, better customer stickiness</li>
                            <li><strong>Processing types:</strong> vIBANs, e-wallets, payment account services (via partners)</li>
                        </ul>
                        <div className="packages-strip">
                            <span className="tag">API</span>
                            <span className="tag">Widget / iFrame</span>
                            <span className="tag">White-label EverWallet</span>
                        </div>
                        <div className="risk-card">
                            <strong>Risk disclosure</strong>
                            Fiat services offered through third parties or partner entities should be identified with the relevant licensing disclosure.
                        </div>
                    </div>
                    <div className="infra-band example-card">
                        <span className="kicker">Use case</span>
                        <h3>Wallet utility beyond one transaction</h3>
                        <p>A business launches euro balances and vIBANs so users can fund, hold, and pay from a branded wallet experience.</p>
                        <div className="case-metrics">
                            <span className="metric-pill">Named fiat accounts</span>
                            <span className="metric-pill">More repeat usage</span>
                            <span className="metric-pill">Branded wallet experience</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="stablecoins">
                <div className="container solution-block">
                    <div className="feature-card">
                        <span className="kicker">Solution 04</span>
                        <h2>Stablecoins</h2>
                        <p>Power payments and stablecoin infrastructure services (subject to regulatory permissions) on a managed wallet and compliance stack.</p>
                        <ul className="check-list micro-list">
                            <li><strong>Features:</strong> stablecoin-based payment flows (subject to market and issuer risks), wallet distribution, program controls</li>
                            <li><strong>Benefits:</strong> faster settlement design, programmable movement, and enhanced treasury management flexibility</li>
                            <li><strong>Processing types:</strong> payments, stablecoin infrastructure services</li>
                        </ul>
                        <div className="packages-strip">
                            <span className="tag">API</span>
                            <span className="tag">Widget / iFrame</span>
                            <span className="tag">White-label EverWallet</span>
                        </div>
                        <div className="risk-card">
                            <strong>Risk disclosure</strong>
                            Any stablecoin-specific marketing must stay consistent with the relevant white paper and current disclosures.
                        </div>
                    </div>
                    <div className="infra-band example-card">
                        <span className="kicker">Use case</span>
                        <h3>Stablecoin-based value for payouts and settlement</h3>
                        <p>A merchant or platform runs payment and payout flows on a stablecoin-based rail with wallet controls and monitoring built in.</p>
                        <div className="case-metrics">
                            <span className="metric-pill">Programmable payments</span>
                            <span className="metric-pill">Faster settlement design</span>
                            <span className="metric-pill">Treasury flexibility</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="packages">
                <div className="container">
                    <div className="section-head">
                        <span className="kicker">Packages</span>
                        <h2>Choose how each solution is consumed</h2>
                        <p>Use the same product stack with different implementation depth.</p>
                    </div>
                    <div className="package-grid">
                        <article className="card">
                            <div className="icon-badge">
                                <img src="/icons/api.svg" alt="API icon" />
                            </div>
                            <h3>API</h3>
                            <p>Best for direct integration, custom logic, and internal orchestration.</p>
                        </article>
                        <article className="card">
                            <div className="icon-badge">
                                <img src="/icons/widget-iframe.svg" alt="Widget iFrame icon" />
                            </div>
                            <h3>Widget / iFrame</h3>
                            <p>Best for faster launch and lower engineering lift.</p>
                        </article>
                        <article className="card">
                            <div className="icon-badge">
                                <img src="/icons/white-label.svg" alt="White label icon" />
                            </div>
                            <h3>White-label EverWallet</h3>
                            <p>Best for a branded wallet and payment front end on Everest infrastructure.</p>
                        </article>
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container">
                    <div className="infra-band">
                        <span className="kicker" style={{ color: "#cfe5ff" }}>Embedded in every solution</span>
                        <h3>Operational stack</h3>
                        <div className="infra-grid">
                            <div className="infra-item">
                                <strong>KYC / AML</strong>
                                <span>Onboarding and screening tools integrated into the customer flow to support compliance processes.</span>
                            </div>
                            <div className="infra-item">
                                <strong>Transaction monitoring</strong>
                                <span>Controls around payment and wallet activity.</span>
                            </div>
                            <div className="infra-item">
                                <strong>Custody (where licensed and applicable)</strong>
                                <span>Managed digital-asset handling and wallet control.</span>
                            </div>
                            <div className="infra-item">
                                <strong>Wallet infrastructure</strong>
                                <span>Ledger, account, and wallet services under every package.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Solutions;
